import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@clariness/modal';
import { Button } from '@clariness/button';
import { Box } from '@clariness/box';
import { Flex } from '@clariness/flex';
import { Dropdown } from '@clariness/dropdown';
import { Text, TextField } from '@clariness/clarikit-react';
import { useIntl } from 'react-intl';
import { Services } from 'services';
import { Store } from 'store';
import { useFormValidation } from 'utils/validation';
import { getManualReferralEligibleAssignments } from 'services/referrals/create-manual-referral/eligibility';
import {
  AddExternalReferralFormValues,
  createAddExternalReferralSchema,
} from './validation';

export const AddExternalReferralModal = () => {
  const intl = useIntl();
  const { closeModal } = Store.useModal();
  const { user } = Store.useUser();
  const { data } = Services.SiteEmployees.useGetInvestigatorStudies();
  const { createExternalReferral, isLoading } =
    Services.Referrals.useCreateExternalReferral({
      onSuccess: () => {
        closeModal();
      },
    });

  const schema = useMemo(
    () => createAddExternalReferralSchema(intl.formatMessage.bind(intl)),
    [intl]
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormValidation<AddExternalReferralFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      studyId: '',
      siteId: '',
      firstName: '',
      lastName: '',
      telephone: '',
      year: '',
      zip: '',
      email: '',
      referralSource: '',
    },
  });

  const studyId = watch('studyId');
  const siteId = watch('siteId');

  const sortByName = <T extends { name: string }>(items: T[]): T[] =>
    items.sort((left, right) => left.name.localeCompare(right.name));

  const eligibleAssignments = useMemo(
    () =>
      getManualReferralEligibleAssignments(
        data?.investigatorNotificationUpdateDto,
        user
      ),
    [data?.investigatorNotificationUpdateDto, user]
  );

  const studyOptions = useMemo(() => {
    const byStudyId = Object.fromEntries(
      eligibleAssignments.map((assignment) => [
        assignment.studyId,
        { id: assignment.studyId, name: assignment.studyName },
      ])
    );
    return sortByName(Object.values(byStudyId));
  }, [eligibleAssignments]);

  const siteOptions = useMemo(() => {
    const bySiteId = Object.fromEntries(
      eligibleAssignments
        .filter((assignment) => assignment.studyId === studyId)
        .map((assignment) => [
          assignment.siteId,
          { id: assignment.siteId, name: assignment.siteName },
        ])
    );
    return sortByName(Object.values(bySiteId));
  }, [eligibleAssignments, studyId]);

  // Clear siteId if it is not in the eligible assignments
  useEffect(() => {
    if (siteOptions.some((site) => site.id === siteId)) {
      return;
    }

    setValue('siteId', '');
  }, [siteOptions, siteId, setValue]);

  const onSubmit = (values: AddExternalReferralFormValues) => {
    createExternalReferral({
      studyId: values.studyId,
      siteId: values.siteId,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      telephone: values.telephone.trim(),
      year: values.year.trim() ? Number(values.year.trim()) : undefined,
      zip: values.zip.trim() || undefined,
      email: values.email.trim(),
      referralSource: values.referralSource.trim() || undefined,
    });
  };

  const firstNameRegister = register('firstName');
  const lastNameRegister = register('lastName');
  const telephoneRegister = register('telephone');
  const yearRegister = register('year');
  const zipRegister = register('zip');
  const emailRegister = register('email');
  const referralSourceRegister = register('referralSource');

  const formStackSx = {
    flexDirection: 'column' as const,
    gap: '0.75rem',
    width: '100%',
    maxWidth: '100%',
    alignItems: 'stretch' as const,
  };

  const fullWidthFieldSx = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  };

  const textFieldWrapSx = {
    ...fullWidthFieldSx,
    '& > *': {
      width: '100%',
      maxWidth: '100%',
    },
  };

  const studySourcePanelSx = {
    width: '100%',
    padding: '1rem',
    borderRadius: '0.375rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'text4',
    backgroundColor: 'hgl7',
    boxSizing: 'border-box' as const,
  };

  const patientSectionSx = {
    width: '100%',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'text4',
    paddingTop: '1rem',
    marginTop: '0.25rem',
    boxSizing: 'border-box' as const,
  };

  return (
    <Modal
      sx={{
        width: 'min(92vw, 38.75rem)',
      }}
      onClose={closeModal}
    >
      <Modal.Header
        title={intl.formatMessage({
          id: 'is.add_external_referral.title',
          defaultMessage: 'Add Patient+',
        })}
        sx={{
          height: '4.625rem',
        }}
      />

      <Modal.Body
        sx={{
          padding: '1.25rem',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Flex
          sx={{
            ...formStackSx,
            gap: '1rem',
          }}
        >
          <Box sx={studySourcePanelSx}>
            <Box marginBottom="0.75rem">
              <Text as="p" variant="bodyMd" fontWeight="bold">
                {intl.formatMessage({
                  id: 'is.add_external_referral.section_study_site',
                  defaultMessage: 'Study & site',
                })}
              </Text>
            </Box>
            <Flex
              sx={{
                flexDirection: 'column',
                gap: '1rem',
                width: '100%',
                alignItems: 'stretch',
              }}
            >
              <Box sx={fullWidthFieldSx}>
                <Box marginBottom="0.5rem">
                  <Text as="p" variant="bodySm">
                    {intl.formatMessage({
                      id: 'is.add_external_referral.study_label',
                      defaultMessage: 'Study',
                    })}
                  </Text>
                </Box>
                <Controller
                  name="studyId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      sx={fullWidthFieldSx}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(
                          (event.target as HTMLSelectElement).value
                        );
                        setValue('siteId', '');
                      }}
                      error={fieldState.error?.message}
                      placeholder={intl.formatMessage({
                        id: 'is.add_external_referral.study_placeholder',
                        defaultMessage: 'Select study *',
                      })}
                      data-test-id="add-external-referral-study-select"
                    >
                      <Dropdown.Option value="">
                        {intl.formatMessage({
                          id: 'is.add_external_referral.study_placeholder',
                          defaultMessage: 'Select study *',
                        })}
                      </Dropdown.Option>
                      {studyOptions.map((study) => (
                        <Dropdown.Option key={study.id} value={study.id}>
                          {study.name}
                        </Dropdown.Option>
                      ))}
                    </Dropdown>
                  )}
                />
              </Box>

              <Box sx={fullWidthFieldSx}>
                <Box marginBottom="0.5rem">
                  <Text as="p" variant="bodySm">
                    {intl.formatMessage({
                      id: 'is.add_external_referral.site_label',
                      defaultMessage: 'Site',
                    })}
                  </Text>
                </Box>
                <Controller
                  name="siteId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      sx={fullWidthFieldSx}
                      value={field.value}
                      onChange={(event) =>
                        field.onChange(
                          (event.target as HTMLSelectElement).value
                        )
                      }
                      error={fieldState.error?.message}
                      placeholder={intl.formatMessage({
                        id: 'is.add_external_referral.site_placeholder',
                        defaultMessage: 'Select site *',
                      })}
                      disabled={!studyId}
                      data-test-id="add-external-referral-site-select"
                    >
                      <Dropdown.Option value="">
                        {intl.formatMessage({
                          id: 'is.add_external_referral.site_placeholder',
                          defaultMessage: 'Select site *',
                        })}
                      </Dropdown.Option>
                      {siteOptions.map((site) => (
                        <Dropdown.Option key={site.id} value={site.id}>
                          {site.name}
                        </Dropdown.Option>
                      ))}
                    </Dropdown>
                  )}
                />
              </Box>

              <Box sx={textFieldWrapSx}>
                <Box marginBottom="0.5rem">
                  <Text as="p" variant="bodySm">
                    {intl.formatMessage({
                      id: 'is.add_external_referral.referral_source_label',
                      defaultMessage: 'Referral source',
                    })}
                  </Text>
                </Box>
                <TextField
                  label={intl.formatMessage({
                    id: 'is.add_external_referral.referral_source_label',
                    defaultMessage: 'Referral source',
                  })}
                  labelHidden
                  {...referralSourceRegister}
                  value={watch('referralSource')}
                  onChange={(value) =>
                    referralSourceRegister.onChange({
                      target: { name: referralSourceRegister.name, value },
                    })
                  }
                  error={errors.referralSource?.message}
                  placeholder={intl.formatMessage({
                    id: 'is.add_external_referral.referral_source_placeholder',
                    defaultMessage: 'Referral source (optional)',
                  })}
                  autoComplete="off"
                  data-test-id="add-external-referral-referral-source-input"
                />
                <Box marginTop="0.375rem">
                  <Text as="p" variant="bodySm" color="color-gray-variant-2">
                    {intl.formatMessage({
                      id: 'is.add_external_referral.referral_source_helper',
                      defaultMessage:
                        'Leave blank to store the selected site name as the referral source.',
                    })}
                  </Text>
                </Box>
              </Box>
            </Flex>
          </Box>

          <Box sx={patientSectionSx}>
            <Box marginBottom="0.5rem">
              <Text as="p" variant="bodyMd" fontWeight="bold">
                {intl.formatMessage({
                  id: 'is.add_external_referral.section_patient',
                  defaultMessage: 'Patient details',
                })}
              </Text>
            </Box>
            <Box marginBottom="0.75rem">
              <Text as="p" variant="bodySm">
                {intl.formatMessage({
                  id: 'is.add_external_referral.required_fields',
                  defaultMessage: '*Mandatory fields',
                })}
              </Text>
            </Box>
            <Flex sx={formStackSx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  width: '100%',
                }}
              >
                <Box sx={textFieldWrapSx}>
                  <TextField
                    label="First name"
                    labelHidden
                    requiredIndicator={true}
                    {...firstNameRegister}
                    value={watch('firstName')}
                    onChange={(value) =>
                      firstNameRegister.onChange({
                        target: { name: firstNameRegister.name, value },
                      })
                    }
                    error={errors.firstName?.message}
                    placeholder={intl.formatMessage({
                      id: 'is.add_external_referral.first_name_placeholder',
                      defaultMessage: 'First name *',
                    })}
                    autoComplete="given-name"
                    data-test-id="add-external-referral-first-name-input"
                  />
                </Box>
                <Box sx={textFieldWrapSx}>
                  <TextField
                    label="Last name"
                    labelHidden
                    requiredIndicator={true}
                    {...lastNameRegister}
                    value={watch('lastName')}
                    onChange={(value) =>
                      lastNameRegister.onChange({
                        target: { name: lastNameRegister.name, value },
                      })
                    }
                    error={errors.lastName?.message}
                    placeholder={intl.formatMessage({
                      id: 'is.add_external_referral.last_name_placeholder',
                      defaultMessage: 'Last name *',
                    })}
                    autoComplete="family-name"
                    data-test-id="add-external-referral-last-name-input"
                  />
                </Box>
              </Box>

              <Box sx={textFieldWrapSx}>
                <TextField
                  label="Telephone"
                  labelHidden
                  requiredIndicator={true}
                  type="tel"
                  {...telephoneRegister}
                  value={watch('telephone')}
                  onChange={(value) =>
                    telephoneRegister.onChange({
                      target: { name: telephoneRegister.name, value },
                    })
                  }
                  error={errors.telephone?.message}
                  placeholder={intl.formatMessage({
                    id: 'is.add_external_referral.telephone_placeholder',
                    defaultMessage: 'Phone number *',
                  })}
                  autoComplete="tel"
                  data-test-id="add-external-referral-telephone-input"
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  width: '100%',
                }}
              >
                <Box sx={textFieldWrapSx}>
                  <TextField
                    label="Year"
                    labelHidden
                    type="text"
                    inputMode="numeric"
                    {...yearRegister}
                    value={watch('year')}
                    onChange={(value) =>
                      yearRegister.onChange({
                        target: {
                          name: yearRegister.name,
                          value: value.replace(/\D/g, ''),
                        },
                      })
                    }
                    error={errors.year?.message}
                    placeholder={intl.formatMessage({
                      id: 'is.add_external_referral.year_placeholder',
                      defaultMessage: 'Year of birth',
                    })}
                    autoComplete="off"
                    data-test-id="add-external-referral-year-input"
                  />
                </Box>
                <Box sx={textFieldWrapSx}>
                  <TextField
                    label="ZIP"
                    labelHidden
                    {...zipRegister}
                    value={watch('zip')}
                    onChange={(value) =>
                      zipRegister.onChange({
                        target: { name: zipRegister.name, value },
                      })
                    }
                    placeholder={intl.formatMessage({
                      id: 'is.add_external_referral.zip_placeholder',
                      defaultMessage: 'ZIP',
                    })}
                    autoComplete="postal-code"
                    data-test-id="add-external-referral-zip-input"
                  />
                </Box>
              </Box>

              <Box sx={textFieldWrapSx}>
                <TextField
                  label="Email"
                  labelHidden
                  type="email"
                  requiredIndicator={true}
                  {...emailRegister}
                  value={watch('email')}
                  onChange={(value) =>
                    emailRegister.onChange({
                      target: { name: emailRegister.name, value },
                    })
                  }
                  error={errors.email?.message}
                  placeholder={intl.formatMessage({
                    id: 'is.add_external_referral.email_placeholder',
                    defaultMessage: 'Email address *',
                  })}
                  autoComplete="email"
                  data-test-id="add-external-referral-email-input"
                />
              </Box>
            </Flex>
          </Box>
        </Flex>
      </Modal.Body>

      <Modal.Footer sx={{ padding: '1rem 1.25rem' }}>
        <Flex
          sx={{
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <Button
            variant="secondary"
            onClick={closeModal}
            data-test-id="add-external-referral-cancel-button"
          >
            {intl.formatMessage({
              id: 'is.add_external_referral.cancel_button',
              defaultMessage: 'Cancel',
            })}
          </Button>

          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            data-test-id="add-external-referral-submit-button"
          >
            {intl.formatMessage({
              id: 'is.add_external_referral.submit_button',
              defaultMessage: 'Add patient',
            })}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
